import React, { useEffect } from "react";
import { DeviceAlarmManagerList } from "uis/MeetingRoomDeviceManagement";
import { TMember } from "types/user.types";
import { addDeviceAlarmChannel, deleteDeviceAlarmChannel, getDeviceAlarmChannelList } from "apis/meeting-room-device";
import { useToast } from "hook/useToast";
import { IReceiverMember } from "pages/meeting/complain-manager-setting/uis/table-view/person-table-view";
import { SettingContainer, Title, TopSection, Container } from "./AlarmManagerSettingTab.styles";
import UI from "@mile-meeting/mile-ui";
import { AddMeetingManagerModal, AddMeetingSlackModal } from "uis/modal";
import DeviceAlarmChannelList from "uis/MeetingRoomDeviceManagement/DeviceAlarmChannelList/DeviceAlarmChannelList";
import { IAlarmChannel } from "apis/meeting-room-complain/alarm-channel";
import { getMessengers, TSlackChannel } from "apis/messangers";
import {
  deleteBlockRequestAlarmReceiver,
  deleteBlockRequestAlarmSlackChannel,
  getBlockRequestAlarmReceiverList,
  getBlockRequestAlarmSlackChannelList,
  setBlockRequestAlarmReceiver,
  setBlockRequestAlarmSlackChannelList,
} from "apis/meetings/block-request";

type SettingAlarmHostProps = {
  isShow: boolean;
};

const AlarmManagerSettingTab: React.FC<SettingAlarmHostProps> = ({ isShow }) => {
  const { showToast } = useToast();
  const [alarmReceiverList, setAlarmReceiverList] = React.useState<IReceiverMember[]>([]);
  const [isConnectedSlackApp, setIsConnectedSlackApp] = React.useState<boolean>(false);
  const [openAddManagerModal, setOpenAddManagerModal] = React.useState(false);
  const [openAddSlackModal, setOpenAddSlackModal] = React.useState(false);
  const [channelList, setChannelList] = React.useState<IAlarmChannel[]>([]);

  const onAddManager = async (members: TMember[]) => {
    const res = await setBlockRequestAlarmReceiver(members);
    if (res) {
      if (members.length === 1) showToast(`담당자 "${members[0].nickname}"이 추가되었습니다.`, "success");
      else showToast(`담당자 "${members[0].nickname}"외 ${members.length - 1}인이 추가되었습니다.`, "success");
    } else {
      showToast("담당자 추가에 실패했습니다. 재시도해 주세요.", "error");
    }
    setManagerList();
  };

  const onAddSlackChannel = async (channel: TSlackChannel) => {
    const res = await setBlockRequestAlarmSlackChannelList(channel);
    if (res.success) {
      showToast(`슬랙 채널 "${channel.slackChannelName}"이 추가되었습니다.`, "success");
      setChanelList();
    } else {
      showToast("슬랙 채널 추가에 실패했습니다. 재시도해 주세요.", "error");
    }
  };

  const setManagerList = async () => {
    const res = await getBlockRequestAlarmReceiverList();
    setAlarmReceiverList(res.data);
  };

  const setChanelList = async () => {
    const res = await getBlockRequestAlarmSlackChannelList();
    setChannelList(res.data);
  };

  const onDeleteManager = async (mediumId: string, name: string) => {
    const res = await deleteBlockRequestAlarmReceiver(mediumId);
    if (res.success) {
      showToast(`담당자에서 "${name}"이 삭제되었습니다.`, "success");
      setManagerList();
    } else {
      showToast("담당자 삭제에 실패했습니다. 재시도해 주세요.", "error");
    }
  };

  const onDeleteChannel = async (mediumId: string, name: string) => {
    const res = await deleteBlockRequestAlarmSlackChannel(mediumId);
    if (res.success) {
      showToast(`슬랙 채널에서 "${name}"이 삭제되었습니다.`, "success");
      setChanelList();
    } else {
      showToast("채널 삭제에 실패했습니다. 재시도해 주세요", "error");
    }
  };

  const getIsConnectedSlackApp = async () => {
    const res = await getMessengers();
    if (!res.data) return setIsConnectedSlackApp(false);
    const isConnectedSlack = res.data.some((messenger) => messenger.messengerProvider === "SLACK");
    setIsConnectedSlackApp(isConnectedSlack);
  };

  useEffect(() => {
    if (isShow) {
      setManagerList();
      setChanelList();
      getIsConnectedSlackApp();
    }
  }, [isShow]);

  if (!isShow) return <></>;
  return (
    <Container>
      <SettingContainer>
        <TopSection>
          <Title>
            <UI.Typography
              type="headline01"
              color="var(--alias-color-label-normal)"
              style={{ display: "flex", gap: 6, alignItems: "center" }}
            >
              <p>예약 제한 요청 관리 담당자</p>
              <UI.ToolTip
                position={{ top: -52, left: -137 }}
                text={
                  <p>
                    예약 제한 요청 알림은 슬랙(Slack)만 지원되며,
                    <br />
                    슬랙 미연동 멤버에게는 알림이 발송되지 않습니다.
                  </p>
                }
              >
                <UI.Icon name="slack" size={20} />
              </UI.ToolTip>
            </UI.Typography>
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-alternative)">
              회의실 예약 제한 요청이 발생하면 담당자에게 알림이 전송됩니다.
            </UI.Typography>
          </Title>
          <UI.Button
            title="담당자 추가하기"
            primary={false}
            prefixIcon="addPerson"
            showPrefixIcon
            onClick={() => {
              setOpenAddManagerModal(true);
            }}
          />
        </TopSection>
        <DeviceAlarmManagerList managerList={alarmReceiverList} onHandleDeleteManager={onDeleteManager} />
      </SettingContainer>
      <AddMeetingManagerModal
        existedMembers={alarmReceiverList.map((member) => member.host)}
        modalIsOpen={openAddManagerModal}
        closeModal={() => setOpenAddManagerModal(false)}
        title={"예약 제한 요청 담당자 추가"}
        onHandleAddManager={async (selectedMembers: TMember[]) => {
          await onAddManager(selectedMembers);
          setOpenAddManagerModal(false);
        }}
      />
      <SettingContainer>
        <TopSection>
          <Title>
            <UI.Typography type="headline01" color="var(--alias-color-label-normal)">
              예약 제한 요청 관리 슬랙 채널
            </UI.Typography>
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-alternative)">
              회의실 예약 제한 요청이 발생하면 슬랙 채널로 알림이 전송됩니다.
            </UI.Typography>
          </Title>
          <UI.Button
            disabled={!isConnectedSlackApp}
            title="슬랙 채널 추가하기"
            primary={false}
            prefixIcon="view"
            showPrefixIcon
            onClick={() => {
              if (!isConnectedSlackApp) {
                showToast("슬랙 미연동 상태입니다. 슬랙 연동 후 채널을 추가해 주세요.", "warning");
                return;
              }
              setOpenAddSlackModal(true);
            }}
          />
        </TopSection>
        <DeviceAlarmChannelList channelList={channelList} onHandleDeleteChannel={onDeleteChannel} />
      </SettingContainer>
      <AddMeetingSlackModal
        modalIsOpen={openAddSlackModal}
        closeModal={() => setOpenAddSlackModal(false)}
        onHandleGuide={() => {
          window.open(
            "https://mile-corporation.notion.site/2e6a222a9c1b802a8789cb635c54bd16#2e6a222a9c1b80dea669c7ff85946805"
          );
        }}
        title={"예약 제한 요청 관리 슬랙 채널 추가"}
        onHandleAddChannel={async (channel) => {
          if (!channel) return;
          await onAddSlackChannel(channel);
          setOpenAddSlackModal(false);
        }}
      />
    </Container>
  );
};

export default React.memo(AlarmManagerSettingTab);
